<template>
  <div>
    <DxPopup
      :visible="isVisible"
      title="Price Breakdown"
      :width="650"
      :height="'auto'"
      :drag-enabled="false"
      :hide-on-outside-click="true"
      :show-title="true"
      :showCloseButton="false"
      :wrapperAttr="{ class: 'css-v2 gc-popup' }"
      @shown="onShown"
      @hidden="onHidden"
    >
      <div class="css-v2 transaction-list gc-price-breakdown-wrapper">
        <!-- Item header — matches Customer Item History / GP Tool popup pattern -->
        <div class="informer">
          <GcHeaderContentDynamic
            :baseView="baseView"
            :primaryLabel="lineData.ProductDescription"
            :secondaryLabel="lineData.ItemCode"
            :viewMode="'popup'"
          />
        </div>

        <div class="gc-price-breakdown-content">
          <div class="gc-price-breakdown">
            <!-- Qty / UM -->
            <div class="pb-meta-row">
              <div class="pb-meta-item">
                <span class="pb-meta-label">Qty</span>
                <span class="pb-meta-value">{{
                  formatQty(lineData.SalesUMQty)
                }}</span>
              </div>
              <div class="pb-meta-item">
                <span class="pb-meta-label">UM</span>
                <span class="pb-meta-value">{{ lineData.SalesUM }}</span>
              </div>
            </div>

            <div class="pb-divider"></div>

            <!-- ── Mode A: No price group (simple summary) ── -->
            <template v-if="noPriceCalculationVisible">
              <div class="pb-section-card">
                <div class="pb-row">
                  <span class="pb-label">{{
                    sellVisible ? "Standard Sell" : "Standard Cost"
                  }}</span>
                  <span class="pb-price">{{
                    formatPrice(
                      sellVisible
                        ? lineData.SalesUMStandardPrice
                        : lineData.SalesUMStandardCost,
                    )
                  }}</span>
                </div>
                <div class="pb-row">
                  <span class="pb-label">Calculated Discount</span>
                  <span class="pb-percent"
                    >{{ formatPcnt(lineData.CalculatedDiscPcnt) }}%</span
                  >
                </div>
              </div>

              <div class="pb-section-card pb-section-card--highlight">
                <div class="pb-row">
                  <span class="pb-label">Customer Sell Price</span>
                  <span class="pb-price">{{
                    formatPrice(lineData.SalesUMFXPrice)
                  }}</span>
                </div>
                <div class="pb-row">
                  <span class="pb-label">Transaction Discount</span>
                  <span class="pb-percent"
                    >{{ formatPcnt(lineData.DiscPcnt) }}%</span
                  >
                </div>
              </div>

              <div class="pb-net-row">
                <span class="pb-label pb-label--strong">Net Price</span>
                <span class="pb-price pb-price--net">{{
                  formatPrice(lineData.SalesUMNetFXPrice)
                }}</span>
              </div>
            </template>

            <!-- ── Mode B: Price group active ── -->
            <template v-if="priceCalculationVisible">
              <!-- Standard Sell / Cost -->
              <div class="pb-section-card">
                <div class="pb-row">
                  <span class="pb-label">{{
                    sellVisible ? "Standard Sell" : "Standard Cost"
                  }}</span>
                  <span class="pb-price">{{
                    formatPrice(
                      sellVisible
                        ? lineData.SalesUMStandardPrice
                        : lineData.SalesUMStandardCost,
                    )
                  }}</span>
                </div>
              </div>

              <div class="pb-divider"></div>

              <!-- Price Group -->
              <div class="pb-group-block">
                <div class="pb-group-header">
                  <span class="pb-label pb-label--strong">Price Group</span>
                  <span class="pb-group-meta">
                    <span class="pb-chip">{{ lineData.PriceGroupCode }}</span>
                    <span class="pb-method">{{ normalisedPricingMethod }}</span>
                    <span class="pb-method-value"
                      >{{ pricingMethodValue_Sign
                      }}{{ formatPrice(lineData.PricingMethodValue) }}</span
                    >
                  </span>
                </div>
                <div v-if="priceGroupDescription" class="pb-group-desc">
                  {{ priceGroupDescription }}
                </div>
                <div v-if="priceGroupDateVisible" class="pb-group-dates">
                  {{ formatDate(priceGroupSellStartDate) }} &rarr;
                  {{ formatDate(priceGroupSellEndDate) }}
                </div>

                <div class="pb-group-result-row">
                  <div class="pb-section-card pb-section-card--inline">
                    <div class="pb-row">
                      <span class="pb-label">Price Group Price </span>
                      <span class="pb-price">{{
                        formatPrice(lineData.PriceGroupSalesUMPrice)
                      }}</span>
                    </div>
                  </div>
                  <div class="pb-inline-disc">
                    <span class="pb-label">Calc. Disc.</span>
                    <span class="pb-percent"
                      >{{ formatPcnt(lineData.CalculatedDiscPcnt) }}%</span
                    >
                  </div>
                </div>
              </div>

              <!-- Additional Discount -->
              <div class="pb-group-block pb-group-block--addisc">
                <div class="pb-group-header">
                  <span class="pb-label pb-label--strong"
                    >Additional Discount</span
                  >
                  <span class="pb-group-meta">
                    <span class="pb-chip">{{
                      lineData.AddDiscPriceGroupCode
                    }}</span>
                    <span class="pb-method">{{
                      normalisedAddDiscPricingMethod
                    }}</span>
                    <span class="pb-method-value"
                      >{{ addDiscPricingMethodValue_Sign
                      }}{{
                        formatPrice(lineData.AddDiscPricingMethodValue)
                      }}</span
                    >
                  </span>
                </div>
                <div v-if="addDiscDescription" class="pb-group-desc">
                  {{ addDiscDescription }}
                </div>
                <div v-if="addDiscDateVisible" class="pb-group-dates">
                  {{ formatDate(addDiscSellStartDate) }} &rarr;
                  {{ formatDate(addDiscSellEndDate) }}
                </div>
              </div>

              <div class="pb-divider"></div>

              <!-- Customer Sell + Transaction Discount -->
              <div class="pb-section-card pb-section-card--highlight">
                <div class="pb-row">
                  <span class="pb-label">Customer Sell Price</span>
                  <span class="pb-price">{{
                    formatPrice(lineData.SalesUMFXPrice)
                  }}</span>
                </div>
                <div class="pb-row">
                  <span class="pb-label">Transaction Discount</span>
                  <span class="pb-percent"
                    >{{ formatPcnt(lineData.DiscPcnt) }}%</span
                  >
                </div>
              </div>

              <div class="pb-net-row">
                <span class="pb-label pb-label--strong">Net Price</span>
                <span class="pb-price pb-price--net">{{
                  formatPrice(lineData.SalesUMNetFXPrice)
                }}</span>
              </div>
            </template>
          </div>
        </div>
        <!-- /.gc-price-breakdown-content -->
      </div>

      <div class="footer">
        <DxButton
          text="Back"
          type="default"
          styling-mode="contained"
          @click="onHidden"
        />
      </div>
    </DxPopup>
  </div>
</template>

<script>
import BaseViewMixin from "../v1/_mixins/base.view";
import { SalesOrderService } from "../_services/api/sales/sales-order.service";
import { IsResponseDataValid } from "../_helpers/ui/helpers";
import { formatNumber } from "devextreme/localization";

export default {
  name: "GcPopupPriceBreakdown",
  mixins: [BaseViewMixin],
  props: {
    setVisible: {
      type: Boolean,
      default: false,
    },
    callBack: {
      type: Function,
      default: function () {},
    },
    lineData: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      priceGroupDescription: null,
      addDiscDescription: null,
      priceGroupSellStartDate: null,
      priceGroupSellEndDate: null,
      addDiscSellStartDate: null,
      addDiscSellEndDate: null,
    };
  },
  computed: {
    isVisible() {
      return this.setVisible;
    },

    // Spec §4.1
    priceCalculationVisible() {
      const noPg =
        this.lineData.PriceGroupCode == null ||
        this.lineData.PriceGroupCode == 0 ||
        this.lineData.PriceGroupCode == "0";
      const noAdd =
        this.lineData.AddDiscPriceGroupCode == null ||
        this.lineData.AddDiscPriceGroupCode == 0 ||
        this.lineData.AddDiscPriceGroupCode == "0";

      if (noPg && noAdd) {
        return false;
      }
      if (this.lineData.AddDiscSalesUMPrice > 0) {
        return (
          this.lineData.AddDiscSalesUMPrice == this.lineData.SalesUMFXPrice
        );
      }

      return (
        this.lineData.CalculatedDiscPcnt ==
        this.lineData.CalculatedDiscPcnt_Original
      );
    },

    // Spec §4.2
    noPriceCalculationVisible() {
      return !this.priceCalculationVisible;
    },

    // Spec §4.3
    sellVisible() {
      if (this.noPriceCalculationVisible) return true;
      if (!this.lineData.PricingMethod) return true;
      if (
        this.lineData.PricingMethod === "Markup $" ||
        this.lineData.PricingMethod === "Markup %"
      )
        return false;
      return true;
    },

    // Spec §4.7 — normalise "Special Price" → "Special" (fixes legacy assignment bug §10.1)
    normalisedPricingMethod() {
      return this.lineData.PricingMethod === "Special Price"
        ? "Special"
        : this.lineData.PricingMethod || "";
    },
    normalisedAddDiscPricingMethod() {
      return this.lineData.AddDiscPricingMethod === "Special Price"
        ? "Special"
        : this.lineData.AddDiscPricingMethod || "";
    },

    // Spec §4.5
    pricingMethodValue_Sign() {
      if (!this.lineData.PricingMethod || this.noPriceCalculationVisible)
        return "";
      if (
        this.lineData.PricingMethod === "Markup $" ||
        this.lineData.PricingMethod === "Markup %"
      )
        return "+";
      return this.lineData.PricingMethodValue > 0 ? "-" : "+";
    },
    addDiscPricingMethodValue_Sign() {
      if (!this.lineData.AddDiscPricingMethod || this.noPriceCalculationVisible)
        return "";
      if (
        this.lineData.AddDiscPricingMethod === "Markup $" ||
        this.lineData.AddDiscPricingMethod === "Markup %"
      )
        return "+";
      return this.lineData.AddDiscPricingMethodValue > 0 ? "-" : "+";
    },

    // Spec §4.6
    priceGroupDateVisible() {
      if (!this.priceGroupSellStartDate && !this.priceGroupSellEndDate)
        return false;
      return this.priceCalculationVisible;
    },
    addDiscDateVisible() {
      if (!this.addDiscSellStartDate && !this.addDiscSellEndDate) return false;
      return this.priceCalculationVisible;
    },
  },
  methods: {
    async onShown() {
      this.$emit("show-loading", true);
      this.priceGroupDescription = null;
      this.addDiscDescription = null;
      this.priceGroupSellStartDate = null;
      this.priceGroupSellEndDate = null;
      this.addDiscSellStartDate = null;
      this.addDiscSellEndDate = null;

      const hasPriceGroup =
        this.lineData.PriceGroupCode != null &&
        this.lineData.PriceGroupCode != 0 &&
        this.lineData.PriceGroupCode != "0";

      const hasAddDisc =
        this.lineData.AddDiscPriceGroupCode != null &&
        this.lineData.AddDiscPriceGroupCode != 0 &&
        this.lineData.AddDiscPriceGroupCode != "0";

      // Spec §3.1 — parallel calls, only when ID != 0
      const [pgResult, addDiscResult] = await Promise.all([
        hasPriceGroup
          ? SalesOrderService.getPriceGroupDescription(
              this.lineData.PriceGroupCode,
            )
          : Promise.resolve(null),
        hasAddDisc
          ? SalesOrderService.getPriceGroupDescription(
              this.lineData.AddDiscPriceGroupCode,
            )
          : Promise.resolve(null),
      ]);

      if (pgResult && IsResponseDataValid(pgResult)) {
        const data = pgResult.data.value[0]; // Expecting single result due to filter by Code
        this.priceGroupDescription = data.ShortDescription;
        this.priceGroupSellStartDate = data.SellStartDate || null;
        this.priceGroupSellEndDate = data.SellEndDate || null;
      }
      if (addDiscResult && IsResponseDataValid(addDiscResult)) {
        const data = addDiscResult.data.value[0]; // Expecting single result due to filter by Code
        this.addDiscDescription = data.ShortDescription;
        this.addDiscSellStartDate = data.SellStartDate || null;
        this.addDiscSellEndDate = data.SellEndDate || null;
      }
      this.$nextTick(() => {
        this.$emit("show-loading", false);
      });
    },

    onHidden() {
      this.priceGroupDescription = null;
      this.addDiscDescription = null;
      this.priceGroupSellStartDate = null;
      this.priceGroupSellEndDate = null;
      this.addDiscSellStartDate = null;
      this.addDiscSellEndDate = null;
      this.callBack(false);
    },

    // Spec §6 — 4dp prices
    formatPrice(val) {
      if (val == null || val === "") return "0.0000";
      return formatNumber(Number(val), "##,###,###,###,##0.0000");
    },

    formatQty(val) {
      if (val == null || val === "") return "0.0000";
      return formatNumber(Number(val), "##,###,###,###,##0.0000");
    },

    // Spec §6 — 2dp percentages
    formatPcnt(val) {
      if (val == null || val === "") return "0.00";
      return formatNumber(Number(val), "##,###,###,###,##0.00");
    },

    // Spec §6 — short date format
    formatDate(val) {
      if (!val) return "";
      return new Date(val).toShortDateString();
    },
  },
};
</script>

<style lang="scss">
.gc-price-breakdown-wrapper {
  padding: 12px 16px 0;

  .informer {
    padding-bottom: 8px;
    background-color: white;
    padding: 12px;
  }
}

.gc-price-breakdown-content {
  background-color: #ffffff;
  padding: 12px 16px 16px;
  margin-bottom: 12px;
}

.gc-price-breakdown {
  padding: 0;

  .pb-meta-row {
    display: flex;
    gap: 24px;
    margin-bottom: 8px;
  }

  .pb-meta-item {
    display: flex;
    gap: 8px;
    align-items: baseline;
  }

  .pb-meta-label {
    color: var(--base-text-color-muted, #9e9e9e);
    font-size: 12px;
    min-width: 24px;
  }

  .pb-meta-value {
    font-weight: 600;
    font-size: 13px;
  }

  .pb-divider {
    border-top: 1px solid var(--base-border-color, #e0e0e0);
    margin: 8px 0;
  }

  // Label used in price rows
  .pb-label {
    color: var(--base-text-color-muted, #757575);
    font-size: 13px;
    flex: 1;

    &--strong {
      color: inherit;
      font-weight: 600;
    }
  }

  // Price value — right aligned, tabular nums
  .pb-price {
    font-variant-numeric: tabular-nums;
    font-size: 13px;
    text-align: right;
    min-width: 90px;

    &--net {
      font-weight: 700;
      font-size: 14px;
    }
  }

  .pb-percent {
    font-variant-numeric: tabular-nums;
    font-size: 13px;
    text-align: right;
    min-width: 60px;
    color: var(--base-text-color-muted, #757575);
  }

  // Card: bordered section grouping label+value rows
  .pb-section-card {
    border: 1px solid var(--base-border-color, #e0e0e0);
    border-radius: 4px;
    padding: 6px 10px;
    margin: 6px 0;

    &--highlight {
      background-color: var(--base-bg-subtle, #fafafa);
    }

    &--inline {
      flex: 1;
      margin: 0;
    }
  }

  .pb-row {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    padding: 2px 0;
  }

  // Net price row at the bottom
  .pb-net-row {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    padding: 6px 0 2px;
    border-top: 2px solid var(--base-border-color, #e0e0e0);
    margin-top: 4px;
  }

  // Price group blocks
  .pb-group-block {
    padding: 6px 0;

    &--addisc {
      border-top: 1px dashed var(--base-border-color, #e0e0e0);
      margin-top: 4px;
      padding-top: 8px;
    }
  }

  .pb-group-header {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
  }

  .pb-group-meta {
    display: flex;
    gap: 8px;
    align-items: baseline;
    font-size: 13px;
  }

  .pb-chip {
    background-color: var(--base-bg-subtle, #f0f0f0);
    border-radius: 3px;
    padding: 1px 6px;
    font-size: 12px;
    color: var(--base-text-color-muted, #757575);
  }

  .pb-method {
    font-style: italic;
    color: var(--base-text-color-muted, #757575);
    font-size: 12px;
  }

  .pb-method-value {
    font-variant-numeric: tabular-nums;
    font-size: 13px;
  }

  .pb-group-desc {
    font-size: 12px;
    color: var(--base-text-color-muted, #757575);
    padding: 2px 0 0 0;
  }

  .pb-group-dates {
    font-size: 12px;
    color: var(--base-text-color-muted, #9e9e9e);
    padding: 1px 0;
  }

  .pb-group-result-row {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-top: 6px;
  }

  .pb-inline-disc {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    white-space: nowrap;
    flex-shrink: 0;
    font-size: 12px;
    color: var(--base-text-color-muted, #757575);

    .pb-percent {
      font-size: 12px;
    }
  }
}
</style>
